import React, { useEffect, useState } from "react";
import { listUsers, createUser, updateUser } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Plus, Save } from "lucide-react";
import { toast } from "sonner";

export default function AdminStaff() {
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", role: "editor", password: "" });

  const load = () => listUsers().then(setUsers).catch(() => toast.error("Failed (owner only)"));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newUser.email || !newUser.password || newUser.password.length < 8) return toast.error("Email and password (min 8 chars) required");
    try { await createUser(newUser); toast.success("User created"); setNewUser({ email: "", name: "", role: "editor", password: "" }); setShowNew(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const patch = async (u, updates) => { try { await updateUser(u.id, updates); toast.success("Updated"); load(); } catch { toast.error("Failed"); } };

  return (
    <div data-testid="admin-staff-page">
      <PageHeader eyebrow="Access" title="Staff & Roles" note="Owner: full control · Editor: edit drafts & upload · Reviewer: view only." />
      <div className="mt-8 flex justify-end">
        <button onClick={() => setShowNew(v => !v)} className="btn-maroon" data-testid="staff-new-toggle"><Plus size={14} /> Add staff</button>
      </div>
      {showNew && (
        <div className="mt-4 border bg-white p-6 grid gap-4 sm:grid-cols-2" style={{ borderColor: "var(--gi-border)" }} data-testid="staff-new-form">
          <div><label className="gi-label">Full name</label><input className="gi-input" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} data-testid="staff-new-name" /></div>
          <div><label className="gi-label">Email</label><input className="gi-input" type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} data-testid="staff-new-email" /></div>
          <div><label className="gi-label">Role</label><select className="gi-input" value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })} data-testid="staff-new-role">
            <option value="editor">Editor</option>
            <option value="reviewer">Reviewer</option>
            <option value="owner">Owner</option>
          </select></div>
          <div><label className="gi-label">Temporary password (min 8)</label><input className="gi-input" type="text" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} data-testid="staff-new-password" /></div>
          <div className="sm:col-span-2 flex justify-end"><button onClick={create} className="btn-maroon" data-testid="staff-new-submit"><Save size={12} /> Create staff member</button></div>
        </div>
      )}
      <div className="mt-6 border bg-white" style={{ borderColor: "var(--gi-border)" }}>
        <div className="grid grid-cols-[1fr_1fr_140px_100px_120px] gap-4 px-5 py-3 border-b text-[10px] font-accent" style={{ borderColor: "var(--gi-border)", color: "var(--gi-muted)", letterSpacing: "0.14em" }}>
          <div>Name</div><div>Email</div><div>Role</div><div>Active</div><div>Since</div>
        </div>
        {users.map(u => (
          <div key={u.id} className="grid grid-cols-[1fr_1fr_140px_100px_120px] gap-4 px-5 py-3 border-b last:border-b-0 text-sm items-center" style={{ borderColor: "var(--gi-divider)" }} data-testid={`staff-row-${u.email}`}>
            <div>{u.name}</div>
            <div style={{ color: "var(--gi-muted)" }}>{u.email}</div>
            <div>
              <select className="gi-input" value={u.role} onChange={e => patch(u, { role: e.target.value })} data-testid={`staff-role-${u.email}`}>
                <option value="editor">Editor</option><option value="reviewer">Reviewer</option><option value="owner">Owner</option>
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={u.active !== false} onChange={e => patch(u, { active: e.target.checked })} data-testid={`staff-active-${u.email}`} /> Active</label>
            </div>
            <div className="text-xs" style={{ color: "var(--gi-muted)" }}>{u.created_at?.slice(0, 10)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
